import { h, Component } from 'preact';
import { FinanceSpinner } from '../glyphs';
import { shallowEqual } from '../../lib/util';
import cx from 'classnames';
import style from './style';
import Footnotes, { FootnoteReference } from '../footnotes';
import { Text } from 'preact-i18n';

/**
 * Get the index or key to access data in a "row".
 * @param {undefined|String[]} columns    the columns of data as returned by CloudQuote API, or undefined if CloudQuote did not return columns
 * @param {String} key                    a property in the response data, from either columns or rows.
 * @returns {Number|String}               returns an index in the columns array if columns is not empty, otherwise returns the key itself
 * @example
 * Access the key "Date" in `row` when there are columns
 *   let columns = [ 0: 'Date', 1: 'Price' ];
 *   let row = [ 0: 999, 1: 50 ];
 *   assert(row[indexOrKey(columns, 'Date')] === 999);
 * Accessing the key "Date" in `row` when there are not columns
 *   let columns = undefined;
 *   let row = { Date: 999, Price: 50 };
 *   assert(row[indexOrKey(columns, 'Date')] === 999);
 */
export function indexOrKey(columns, key) {
	return columns && columns.length ? columns.indexOf(key) : key;
}

/**
 * Create a table which fetchs data from CloudQuote and displays it in an easy to format data table.
 * @param {Object} options                           The options for displaying the DataTable.
 * @param {(String|Function)[]} [options.headers]    the labels for all columns headers.
 * @param {(String|Function)[]} [options.cells]      the data to display in each cell for all rows.
 * @param {Function} [options.apiMethod]             the CloudQuote API method from @os/cloudquote that the table will get data from. this function will be called with DataTable.props.params
 * @param {Function} [options.sort]                  a function for sorting data. this function is called with data and returns sorted data. eg. (data) => data.
 * @param {Number} [options.maxRows]                 the data will be truncated to contain only maxRows rows.
 */
export default function createDataTable({ headers, cells, apiMethod, sort, maxRows, footnotes }) {
	return class DataTable extends Component {

		state = { data: undefined };

		getParams({ symbol, params }) {
			if (symbol) {
				if (!params) {
					params = { symbol };
				}
				else {
					params.symbol = symbol;
				}
			}

			return params;
		}

		fetchData(props) {
			let { sort, apiMethod, maxRows } = props;
			let params = this.getParams(props);
			return apiMethod(params)
				.then((data) => typeof sort === 'function' ? sort(data) : data)
				.then(({ rows, columns }) => ({ columns, rows: rows && rows.slice(0, maxRows) }))
				.then((data) => this.setState({ data }));
		}

		static defaultProps = { headers, cells, apiMethod, sort, maxRows, footnotes };

		componentWillMount() {
			if (!this.props.data && this.props.apiMethod) {
				this.fetchData(this.props);
			}
		}

		componentWillReceiveProps(nextProps) {
			if (!nextProps.data && !shallowEqual(this.getParams(this.props), this.getParams(nextProps))) {
				this.setState({ data: {} }, () => {
					this.fetchData(nextProps);
				});
			}
		}

		render({ headers, cells, footnotes, ...props }, { data }) {
			data = props.data || data;
			let isLoading = !data && (
				<div>
					<FinanceSpinner />
				</div>
			);


			let hasNoData = data && (!data.rows || !data.rows.length) && (
				<NoResultsTable
					data={data}
					headers={headers}
				/>
			);

			return (
				<div class={props.class} style={props.style}>
					{ hasNoData || isLoading || (
						<Table
							data={data}
							headers={headers}
							cells={cells}
							footnotes={footnotes}
						/>
					)}
					<Footnotes notes={footnotes} />
				</div>
			);
		}
	};
}

const NoResultsTable = ({ data, headers, messageId }) => (
	<table class={[cx(style.financeTable), style.noResults].join(' ')}>
		<Thead data={data} headers={headers} />
		<TbodyNoResults colspan={headers.length} />
	</table>
);

const Table = ({ data, headers, cells, footnotes }) => (
	<table class={cx(style.financeTable)}>
		<Thead data={data} headers={headers} footnotes={footnotes} />
		<Tbody data={data} cells={cells} />
	</table>
);

const TableHeading = ({ header, footnotes, data }) => {
	let headerText;
	if (typeof header === 'function') {
		headerText = header(data);
	}
	// If a vNode, don't try to make a FootnoteReference component out of it
	else if (typeof header === 'object' && !header.nodeName) {
		headerText = <span>{header.name}<FootnoteReference id={header.id} footnotes={footnotes} /></span>;
	}
	else {
		headerText = header;
	}
	return <th>{headerText}</th>;
};

const Thead = ({ data, headers, footnotes }) => (
	<thead>
		<tr>
			{headers
				.map((header) => (
					<TableHeading header={header} footnotes={footnotes} data={data} />
				))
			}
		</tr>
	</thead>
);

/**
 * Body of a data table.
 * @param {Object} props                          preact props
 * @param {Object} [props.data]                   data from a cloudquote API response, shaped as { rows, columns }
 * @param {(String|Function)[]} [props.cells]     how to display the cells in each row. the number of columns in a table is equal to the length of props.cells. if given a string, will display row[columns.indexOf(cell)]. if given a function, will call that function with all columns and the current row, allowing you to use functions to format cells with a callback.
 * @example
 * To create a table with two rows, and two cells in each row, formatted to two decimal places.
 *   <Tbody
			data={{
				columns: [ 0: 'High', 1: 'Low' ],
				rows: [
					[ 0: 24, 1: 12 ],
					[ 0: 20, 1: 10 ]
				]
			}}
			cells={[
				({ columns, row }) => return parseFloat(row[columns.indexOf('High')]).toFixed(2),
				({ columns, row }) => return parseFloat(row[columns.indexOf('Low')]).toFixed(2)
			]}
		/>
 */
const Tbody = ({ data, cells }) => {
	let { rows, columns } = data;
	return (
		<tbody>
			{rows
				.map((row) => (
					<tr>
						{cells
							.map((cell) => (
								<td>
									{typeof cell === 'function'
										? cell({ columns, row })
										: row[indexOrKey(columns, cell)] || ''
									}
								</td>
							))
						}
					</tr>
				))
			}
		</tbody>
	);
};

const TbodyNoResults = ({ colspan }) => (
	<tbody>
		<tr>
			<td colspan={colspan}><Text id="dataUnavailable">Data Unavailable</Text></td>
		</tr>
	</tbody>
);

/**
 * Handy functions for using with the cloudquote DataTable
 * @property {Function} formatField    pass in a field and a callback to call when printing that field
 * @property {Object} sort             a few examples of different sorting functions
 */
export const tableUtils = {
	formatField: function formatField(field, fn) {
		return ({ columns, row }) => (
			fn(row[indexOrKey(columns, field)])
		);
	},
	sort: {
		descending: {
			dateByKey: function descendingDateByKey(key) {
				return ({ columns, rows }) => {
					rows = rows && rows.slice().sort((a, b) => (
						new Date(b[indexOrKey(columns, key)]) - new Date(a[indexOrKey(columns, key)])
					));
					return { columns, rows };
				};
			},
			byKey: function descendingByKey(key) {
				return ({ columns, rows }) => {
					rows = rows && rows.slice().sort((a, b) => (
						b[indexOrKey(columns, key)] - a[indexOrKey(columns, key)]
					));
					return { columns, rows };
				};
			}
		}
	}
};
